/**
 * CYANNECT-PRICING-FINAL · public pricing sections for Basic / Starter / Pro.
 * Rendered only when GET /api/pricing/current returns final_active=true. No client-side money math —
 * every amount is a string from the backend.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Sparkles, Store, Gift, CreditCard } from "lucide-react";

// Shown on the Basic card only (approved 2026-10).
const BASIC_NOT_INCLUDED = ["No MCP Connector included", "No premium AI creator included"];
import { cadStr } from "@/lib/pricing";

const TAGLINES = {
  basic: "No monthly fee. You pay only when Cyannect delivers a verified sale.",
  starter: "Lower fees for merchants running steady campaigns.",
  growth: "Our lowest fees, more storefronts and AI-assisted campaigns.",
};

function PriceBlock({ plan, billing }) {
  const annual = billing === "annual";
  const free = plan.monthly_cad === "0.00";
  return (
    <div className="mt-6" data-testid={`final-plan-${plan.id}-price-block`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <div className="text-4xl xl:text-5xl font-display font-medium text-slate-900 whitespace-nowrap" data-testid={`final-plan-${plan.id}-price`}>
          {cadStr(annual ? plan.annual_cad : plan.monthly_cad)}
        </div>
        <div className="text-slate-500 text-sm">/ {annual ? "year" : "month"}</div>
      </div>
      <div className="mt-2 text-sm text-slate-500" data-testid={`final-plan-${plan.id}-billing-note`}>
        {free ? "No subscription fee on either billing option."
          : annual ? <><strong className="text-emerald-700" data-testid={`final-plan-${plan.id}-annual-free`}>2 months free</strong> — 12 months of service for the price of 10 · or {cadStr(plan.monthly_cad)} monthly.</>
            : <>Billed monthly, or {cadStr(plan.annual_cad)}/year — 2 months free.</>}
      </div>
    </div>
  );
}

function FinalPlanCard({ plan, billing }) {
  const rec = plan.id === "growth";
  return (
    <div data-testid={`final-plan-${plan.id}`}
         className={`relative rounded-3xl p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${
           rec ? "bg-white border-2 border-emerald-400 shadow-xl" : "bg-white/85 border border-slate-200"}`}>
      {rec && (
        <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-semibold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Most popular
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${rec ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
          <Store className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-2xl text-slate-900" data-testid={`final-plan-${plan.id}-name`}>{plan.name}</div>
          <div className="text-sm text-slate-500">{TAGLINES[plan.id]}</div>
        </div>
      </div>
      <PriceBlock plan={plan} billing={billing} />
      <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid={`final-plan-${plan.id}-fee`}>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Performance Fee</div>
        <div className="text-slate-900 font-medium">
          {plan.fee_pct}% of eligible sale <span className="text-slate-500">or</span> {cadStr(plan.fee_minimum_cad)}, whichever is higher
        </div>
        <div className="text-xs text-slate-500 mt-1">Only on verified, attributed sales completed on your website. Eligible sale = item subtotal after discounts, before tax and shipping.</div>
      </div>
      <ul className="mt-6 space-y-3 text-sm text-slate-700 flex-1" data-testid={`final-plan-${plan.id}-features`}>
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>
        ))}
        {plan.id === "basic" && BASIC_NOT_INCLUDED.map((f) => (
          <li key={f} className="flex items-start gap-2 text-slate-500" data-testid="final-plan-basic-not-included"><Minus className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /><span>{f}</span></li>
        ))}
      </ul>
      <div className="mt-6 text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid={`final-plan-${plan.id}-card-note`}>
        <CreditCard className="w-3.5 h-3.5" /> Card required at signup{plan.monthly_cad === "0.00" ? " — used only for performance fees and add-ons" : ""}
      </div>
      <Link to={`/register?role=merchant&plan=${plan.id}`} data-testid={`final-plan-${plan.id}-cta`}
            className={`mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-medium transition-colors ${
              rec ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white border border-slate-300 text-slate-800 hover:border-slate-500"}`}>
        Start with {plan.name} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export function FinalPlanGrid({ pricing, billing }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6 items-stretch max-w-xl lg:max-w-none mx-auto" data-testid="final-plan-grid">
      {pricing.plans.map((p) => <FinalPlanCard key={p.id} plan={p} billing={billing} />)}
    </div>
  );
}

const Yes = ({ testid }) => <Check className="w-4 h-4 text-emerald-600 mx-auto" data-testid={testid} />;
const No = ({ testid }) => <Minus className="w-4 h-4 text-slate-300 mx-auto" data-testid={testid} />;

export function FinalAddons({ pricing }) {
  const ids = pricing.plans.map((p) => p.id);
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="final-addons">
      <div className="max-w-2xl mb-8">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Add-ons</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">Grow coverage when you need it.</h2>
        <p className="mt-3 text-slate-600">Add-on prices are the same on every plan. Basic can add extra provinces and storefronts. Every extra storefront includes 3 provinces.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/85">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-5 py-4 font-semibold">Add-on</th>
              <th className="text-right px-5 py-4 font-semibold">Monthly</th>
              <th className="text-right px-5 py-4 font-semibold">Annual</th>
              {pricing.plans.map((p) => <th key={p.id} className="text-center px-4 py-4 font-semibold">{p.name}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pricing.addons.map((a) => (
              <tr key={a.id} data-testid={`final-addon-${a.id}`}>
                <td className="px-5 py-4"><div className="font-medium text-slate-900">{a.name}</div><div className="text-xs text-slate-500">{a.entitlement}</div></td>
                <td className="px-5 py-4 text-right font-mono" data-testid={`final-addon-${a.id}-monthly`}>{cadStr(a.monthly_cad)}</td>
                <td className="px-5 py-4 text-right font-mono">{a.annual_cad ? cadStr(a.annual_cad) : "—"}</td>
                {ids.map((id) => (
                  <td key={id} className="px-4 py-4 text-center">
                    {a.eligible[id] ? <Yes testid={`final-addon-${a.id}-${id}-yes`} /> : <No testid={`final-addon-${a.id}-${id}-no`} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function FinalFeeExamples({ pricing }) {
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="final-fee-examples">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold mb-2">Transparent fees</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900">What you pay on a verified sale</h2>
        <p className="mt-4 text-slate-600">Fee = the percentage of the eligible sale value or the plan minimum, whichever is higher. Marked rows hit the minimum.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/85">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-6 py-4 font-semibold">Eligible sale</th>
              {pricing.plans.map((p) => (
                <th key={p.id} className="text-right px-6 py-4 font-semibold">{p.name} ({p.fee_pct}% or {cadStr(p.fee_minimum_cad)})</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pricing.fee_examples.map((r) => (
              <tr key={r.sale_cad} data-testid={`final-fee-row-${r.sale_cad}`}>
                <td className="px-6 py-4 font-medium text-slate-800">{cadStr(r.sale_cad)}</td>
                {pricing.plans.map((p) => (
                  <td key={p.id} className="px-6 py-4 text-right font-mono text-slate-800" data-testid={`final-fee-${p.id}-${r.sale_cad}`}>
                    {cadStr(r[p.id].fee_cad)}{r[p.id].minimum_applied && <span className="ml-1 text-[10px] text-amber-700 font-sans">min</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function FinalCashback({ pricing }) {
  const rate = pricing.shopper_cashback?.standard_rate_display;
  return (
    <section className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-testid="final-cashback">
      <div className="rounded-3xl bg-gradient-to-br from-cyan-50 to-emerald-50 border border-cyan-200 p-10 flex flex-col md:flex-row gap-8 items-start">
        <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center"><Gift className="w-7 h-7 text-emerald-700" /></div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-cyan-800 font-semibold mb-2">Shopper cashback</div>
          <h2 className="font-display text-3xl md:text-4xl text-slate-900">Cyannect funds shopper rewards — you do not.</h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            Every verified purchase earns the shopper <strong data-testid="final-cashback-rate">{rate} cashback</strong> on the eligible sale value — the same rate on Basic, Starter and Pro. Shoppers can turn cashback into gift cards from <strong data-testid="final-cashback-gift-min">{cadStr(pricing.gift_card_min_redemption_cad)}</strong>.
          </p>
        </div>
      </div>
    </section>
  );
}

const planPrices = (pricing) => pricing.plans.filter((p) => p.monthly_cad !== "0.00")
  .map((p) => `${p.name} ${cadStr(p.monthly_cad)}/month or ${cadStr(p.annual_cad)}/year`).join("; ");

export function finalFaq(pricing) {
  const name = (id) => pricing.plans.find((p) => p.id === id)?.name;
  return [
    { q: `Why does ${name("basic")} need a card?`, a: `${name("basic")} has no monthly fee, but Performance Fees on verified sales and any add-ons you choose are charged to the card you add at signup.` },
    { q: `I already subscribe to ${name("starter")} or ${name("growth")}. Does my price change?`, a: `The period you have already paid for stays at the price you paid — nothing is re-billed. From your next renewal your subscription moves to the current price for your plan (${planPrices(pricing)}), and you get the new plan allowances. Verified sales from the pricing start use the new Performance Fee for your plan; earlier sales keep the fee they were recorded with.` },
    { q: `Can I move down to ${name("basic")}?`, a: `Yes. A downgrade takes effect at the end of the period you've already paid for. Your storefronts, campaigns and history stay in place — only future features and fees change.` },
    { q: "What do annual plans include?", a: `Annual ${name("starter")} and ${name("growth")} give you 12 months of service for the price of 10 months.` },
    { q: "Which features need an upgrade?", a: `AI-assisted coupon creation comes with ${name("starter")} and ${name("growth")}. The other AI tools, MCP connectors and cohort analysis come with ${name("growth")}. ${name("basic")} shows headline totals for the last 30 days; ${name("starter")} and ${name("growth")} add the full analytics views.` },
    { q: "What is the eligible sale value?", a: "The item subtotal after discounts, before sales tax and shipping. Performance Fees and shopper cashback are both calculated on it." },
    { q: "Who pays for shopper cashback?", a: `Cyannect funds it. Shoppers earn ${pricing.shopper_cashback?.standard_rate_display} on every verified purchase, whichever plan you're on.` },
  ];
}
